/**
 * GeoSection — "where are your customers" map + top states, embedded on the
 * Marketing page (2026-06-12 IA decision: geography is an acquisition VIEW,
 * not a destination — no competitor ships a standalone geography page, and
 * the State dimension already lives in every drill).
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../lib/api-context.js";
import { Card, CardHead, moneyFmt } from "./cards.js";
import { Segmented } from "./Segmented.js";
import { RankedBars } from "./RankedBars.js";
import { ChoroplethMap } from "./ChoroplethMap.js";
import { useDrill } from "../lib/drill.js";
import { useSetupGate } from "../lib/setup-gate.js";
import { usePeriod } from "../lib/period.js";
import { per100k } from "../lib/us-population.js";
import type { Capabilities } from "../lib/card-requirements.js";
import { RevenueCmToggle, useStickyRevenueCmMode } from "./RevenueCmToggle.js";
import { PageLoadError } from "./PageLoadError.js";
import { pageLoadState } from "../lib/page-load-state.js";

interface StateRow { state: string; customers: number; orders: number; revenue: number; valuePerCustomer: number }
interface GeoResponse { states: StateRow[]; unmappedCustomers: number }

type MapMetric = "revenue" | "customers" | "valuePerCustomer" | "per100k";
const METRICS: Array<{ value: MapMetric; label: string }> = [
  { value: "revenue", label: "Revenue" },
  { value: "customers", label: "Customers" },
  { value: "valuePerCustomer", label: "$ / customer" },
  { value: "per100k", label: "Per 100k people" },
];

export function GeoSection({ caps }: { caps?: Capabilities }): React.ReactElement | null {
  const api = useApiClient();
  const gate = useSetupGate();
  const drill = useDrill();
  const period = usePeriod();
  const [metric, setMetric] = useState<MapMetric>("revenue");
  const [mapBasis, setMapBasis] = useStickyRevenueCmMode("dd-mode-geo-map");
  const [rankBasis, setRankBasis] = useStickyRevenueCmMode("dd-mode-geo-rank");
  const ready = gate.data?.previewAllowed === true && gate.data.syncStatus === "ready";
  const hasCm = Boolean(caps?.contributionMargin && !caps.cmGap);
  const geographyQ = useQuery({
    queryKey: ["geography", period.cacheKey],
    queryFn: () => api.get<GeoResponse>("/dashboard/geography", { ...period.query }),
    enabled: ready,
  });
  const cmByStateQ = useQuery({
    queryKey: ["geography-cm", period.cacheKey],
    queryFn: () => api.get<{ rows: Array<{ key: string; value: number }> }>("/dashboard/breakdown", { metric: "cm", dimension: "state", ...period.query }),
    enabled: ready && hasCm,
  });
  const requiredLoad = pageLoadState(geographyQ, cmByStateQ);
  if (requiredLoad.failed) {
    return (
      <PageLoadError
        title="We couldn't load your marketing numbers"
        retrying={requiredLoad.retrying}
        onRetry={requiredLoad.retry}
      />
    );
  }
  const data = geographyQ.data;
  // Null WHOLESALE (header included) while loading or for non-US stores —
  // an orphaned section heading over an empty grid reads as broken.
  if (!data || data.states.length === 0) return null;
  if (hasCm && (mapBasis === "profit" || rankBasis === "profit") && (cmByStateQ.isLoading || cmByStateQ.isPlaceholderData)) {
    return <div role="status" style={{ color: "var(--body)", fontSize: 13, padding: "18px 0" }}>Loading…</div>;
  }

  const states = data.states;
  const cmByState = new Map((cmByStateQ.data?.rows ?? []).map((row) => [row.key, row.value]));
  const mapUsesCm = mapBasis === "profit" && hasCm;
  const rankUsesCm = rankBasis === "profit" && hasCm;
  const moneyValue = (state: StateRow, cm: boolean): number => cm ? (cmByState.get(state.state) ?? 0) : state.revenue;
  const top = [...states]
    .sort((a, b) => moneyValue(b, rankUsesCm) - moneyValue(a, rankUsesCm))
    .slice(0, 12);
  // Build the choropleth's per-state values for the selected metric (per-100k is
  // the market-penetration view — customers per 100k residents).
  const metricVal = (s: StateRow): number => {
    if (metric === "per100k") return per100k(s.state, s.customers) ?? 0;
    if (metric === "customers") return s.customers;
    if (metric === "valuePerCustomer") return s.customers > 0 ? moneyValue(s, mapUsesCm) / s.customers : 0;
    return moneyValue(s, mapUsesCm);
  };
  const mapValues: Record<string, number> = {};
  for (const s of states) mapValues[s.state] = metricVal(s);
  const mapMoney = metric === "revenue" || metric === "valuePerCustomer";
  const mapLabel = metric === "revenue" && mapUsesCm
    ? "CM"
    : metric === "valuePerCustomer" && mapUsesCm
      ? "CM / customer"
      : METRICS.find((m) => m.value === metric)?.label ?? "value";
  // Drill follows the map's selected metric where a drill metric exists
  // ($/customer and per-100k are derived views — revenue is their best proxy).
  const openState = (st: string): void => {
    const m = metric === "customers" || metric === "per100k" ? "customers" : mapUsesCm ? "cm" : "revenue";
    drill.open({ title: `${st} — ${m === "customers" ? "customers" : m === "cm" ? "contribution margin" : "revenue"}`, metric: m, dimension: "month", filters: { state: st } });
  };

  return (
    <section style={{ marginTop: 30, paddingTop: 22, borderTop: "1px solid var(--border)" }}>
      <div style={{ margin: "0 0 14px" }}>
        <h2 style={{ fontSize: 17, margin: 0, display: "flex", alignItems: "center", gap: 9 }}>
          <span style={{ width: 4, height: 17, background: "var(--accent)", borderRadius: 2, display: "inline-block" }} />
          Where your customers are · {period.label}
        </h2>
        <p style={{ color: "var(--body)", fontSize: 12.5, margin: "5px 0 0 13px" }}>State-level view of demand — click any state to drill, and every drill can also view by state.</p>
      </div>
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <CardHead title="The map" explain={["<b>What it is:</b> Where your customers, revenue, or contribution margin (CM) come from. Darker states have more of the number you selected.", "<b>How it's calculated:</b> Revenue or CM from the selected orders is grouped by each customer's state. The customer views use those same buyers.", "<b>Example:</b> Switch from Revenue to CM to see whether a high-sales state still ranks high after variable costs.", "<b>Note:</b> Pick a product to narrow every view. Per 100k people compares customer count with state population. States with no orders stay muted."]}>
            {(metric === "revenue" || metric === "valuePerCustomer") && <RevenueCmToggle mode={mapUsesCm ? "profit" : "revenue"} setMode={setMapBasis} cmDisabled={!hasCm} />}
            <Segmented ariaLabel="Map metric" options={METRICS} value={metric} onChange={(v) => setMetric(v as MapMetric)} />
          </CardHead>
        </div>
        <div style={{ marginTop: 14 }}>
          <ChoroplethMap values={mapValues} money={mapMoney} valueLabel={mapLabel} onPick={openState} />
        </div>
        {data.unmappedCustomers > 0 && (
          <div style={{ marginTop: 10, fontSize: 11.5, color: "var(--faint)" }}>
            {data.unmappedCustomers.toLocaleString()} customers are outside the US (or have no usable state) and aren't on the map.
          </div>
        )}
      </Card>

      <Card style={{ marginBottom: 0 }}>
        <CardHead title={`Top states by ${rankUsesCm ? "CM" : "revenue"}`} explain={["<b>What it is:</b> Your 12 biggest states by revenue or contribution margin (CM), with the value from each customer beside them.", "<b>How it's calculated:</b> Revenue or CM from the selected orders is grouped by each customer's state, then ranked from highest to lowest.", "<b>Example:</b> A state can rank high on Revenue and lower on CM when its product mix costs more to serve.", "<b>Note:</b> Pick a product to narrow both views. CM turns on after every selling product has costs."]}>
          <RevenueCmToggle mode={rankUsesCm ? "profit" : "revenue"} setMode={setRankBasis} cmDisabled={!hasCm} />
        </CardHead>
        <div style={{ marginTop: 14 }}>
          <RankedBars donutToggle rows={top.map((s) => ({ key: s.state, value: moneyValue(s, rankUsesCm), sub: `${moneyFmt(s.customers > 0 ? moneyValue(s, rankUsesCm) / s.customers : 0)}/cust` }))}
            fmt={moneyFmt} onPick={(st) => openState(st)} />
        </div>
      </Card>
    </section>
  );
}
